/**
 * F38 (Wave 2 Track D) — admin subscription API client.
 *
 * Endpoints (plan §8):
 *   GET  /admin/subscription-tiers                        → listTierCatalog
 *   PUT  /admin/subscription-tiers/:code                  → editTierPricing (If-Match + reason)
 *   GET  /admin/venue-subscriptions                       → listVenueSubscriptions (paginated, filters)
 *   GET  /admin/venue-subscriptions/:venueId              → getVenueSubscription (detail + history + invoices)
 *   PUT  /admin/venue-subscriptions/:venueId/override     → overrideSubscription
 *   POST /admin/venue-subscriptions/:venueId/grant-trial  → grantTrial
 *   POST /admin/venue-subscriptions/:venueId/manual-invoice → createManualInvoice
 *
 * Permission gating happens server-side (`subscription_tiers:read/write`,
 * `venue_subscription:override`, reused `payments:read`). The page-level
 * <ProtectedRoute> gates which routes a given admin role can navigate to; the
 * API client forwards requests and relies on the server to enforce RBAC.
 *
 * Subscription INVOICES (statements) are listed/settled/voided via the EXISTING
 * F23 /admin/commission-statements queue (Track D widens its `source` filter
 * with `venue_subscription` + `venue_hybrid`); this client is only for the
 * catalog + the per-venue subscription surface.
 *
 * Component tests do NOT exercise this real client directly — vitest's config
 * alias rewrites `./api/subscription` → `./api/subscription.fixture` (the
 * F23/F35 admin convention).
 */
import type {
  SubscriptionTierCode,
  VenueSubscriptionStatus,
  BillingCadence,
} from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient';

interface DataEnvelope<T> {
  readonly data: T;
}

// Re-export the shared ApiError so callers/tests can `instanceof`-check the
// same class the rest of the admin app throws.
export { ApiError as AdminSubscriptionError } from '../auth/types';

// ─── Read-side view types ────────────────────────────────────────────────────

/** One row of the `subscription_tiers` catalog (admin-editable pricing). */
export interface AdminSubscriptionTier {
  readonly code: SubscriptionTierCode;
  readonly displayName: string;
  readonly sortOrder: number;
  readonly monthlyPriceAmount: number;
  readonly annualPriceAmount: number;
  readonly priceCurrency: string;
  readonly commissionBasisPoints: number;
  readonly isTrialDefault: boolean;
  readonly updatedAt: string;
}

export interface AdminTierCatalogResponse {
  readonly data: readonly AdminSubscriptionTier[];
}

/** A plan_history audit entry rendered in the AuditLogTimeline. */
export interface AdminSubscriptionHistoryEntry {
  readonly id: string;
  readonly eventKind: string;
  readonly fromTierCode: SubscriptionTierCode | null;
  readonly toTierCode: SubscriptionTierCode | null;
  readonly fromStatus: VenueSubscriptionStatus | null;
  readonly toStatus: VenueSubscriptionStatus | null;
  readonly actorLabel: string | null;
  readonly reason: string | null;
  readonly createdAt: string;
}

/** A subscription invoice (F23 commission_statements row, subscription source). */
export interface AdminSubscriptionInvoice {
  readonly id: string;
  readonly source: string;
  readonly subscriptionTierCode: SubscriptionTierCode | null;
  readonly billingCadence: BillingCadence | null;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly subscriptionFeeAmount: number;
  readonly status: string;
  readonly dueAt: string | null;
}

/** One row of the admin venue-subscriptions list. */
export interface AdminVenueSubscriptionListItem {
  readonly venueId: string;
  readonly venueName: string | null;
  readonly venueSlug: string | null;
  readonly tierCode: SubscriptionTierCode;
  readonly status: VenueSubscriptionStatus;
  readonly billingCadence: BillingCadence;
  readonly trialEndsAt: string | null;
  readonly currentPeriodEnd: string;
  readonly updatedAt: string;
}

export interface AdminVenueSubscriptionListResponse {
  readonly data: readonly AdminVenueSubscriptionListItem[];
  readonly pagination: {
    readonly page: number;
    readonly perPage: number;
    readonly total: number;
  };
}

/**
 * The flat per-venue subscription core (the `venue_subscriptions` row's
 * camelCase projection). This is what the override / grant-trial mutations
 * return — the detail page refetches the full detail after each mutation, so
 * the mutation responses deliberately carry no history/invoices.
 */
export interface AdminVenueSubscriptionCore {
  readonly venueId: string;
  readonly tierCode: SubscriptionTierCode;
  readonly status: VenueSubscriptionStatus;
  readonly billingCadence: BillingCadence;
  readonly trialEndsAt: string | null;
  readonly trialConsumed: boolean;
  readonly currentPeriodStart: string;
  readonly currentPeriodEnd: string;
  readonly queuedSwitchTo: SubscriptionTierCode | null;
  readonly queuedSwitchCadence: BillingCadence | null;
  readonly queuedEffectiveAt: string | null;
  readonly cancelAtPeriodEnd: boolean;
  readonly pastDueSince: string | null;
  readonly updatedAt: string;
}

/**
 * The per-venue subscription detail (current plan + history + invoices).
 * CST-001 — matches the backend's FLAT `VenueSubscriptionDetail`: the
 * subscription columns are spread on the top level + venue display fields +
 * the resolved tier catalog row + plan history (each entry carrying a
 * server-resolved `actorLabel`) + the venue's subscription invoices
 * (commission_statements, sources venue_subscription/venue_hybrid).
 */
export interface AdminVenueSubscriptionDetail extends AdminVenueSubscriptionCore {
  readonly venueName: string | null;
  readonly venueSlug: string | null;
  /** The resolved `subscription_tiers` catalog row for `tierCode`. */
  readonly tier?: AdminSubscriptionTier;
  readonly history: readonly AdminSubscriptionHistoryEntry[];
  readonly invoices: readonly AdminSubscriptionInvoice[];
}

// ─── Request bodies ──────────────────────────────────────────────────────────

export interface EditTierPricingBody {
  readonly monthlyPriceAmount: number;
  readonly annualPriceAmount: number;
  readonly commissionBasisPoints: number;
  readonly reason: string;
}

export interface AdminOverrideSubscriptionBody {
  readonly tierCode?: SubscriptionTierCode | undefined;
  readonly cadence?: BillingCadence | undefined;
  readonly status?: VenueSubscriptionStatus | undefined;
  readonly reason: string;
}

export interface GrantTrialBody {
  readonly reason: string;
  readonly trialDays?: number | undefined;
}

export interface ManualInvoiceBody {
  readonly amount: number;
  readonly reason: string;
}

/**
 * CST-002 — the option names MUST match the backend's .strict()
 * `ListQuerySchema` (`tierCode`, `status`, `venueId`, `q`, `page`, `perPage`).
 * The previous `tier` / `venueQuery` names 422'd on every filtered request.
 */
export interface AdminVenueSubscriptionListOptions {
  readonly page?: number;
  readonly perPage?: number;
  readonly tierCode?: string;
  readonly status?: string;
  readonly q?: string;
}

// ─── API surface ─────────────────────────────────────────────────────────────

export const adminSubscriptionApi = {
  /** GET /admin/subscription-tiers — the 4-tier catalog. */
  listTierCatalog: async (): Promise<AdminTierCatalogResponse> => {
    return adminFetch<AdminTierCatalogResponse>('/admin/subscription-tiers');
  },

  /**
   * PUT /admin/subscription-tiers/:code — edit a tier's pricing + commission bp.
   * `If-Match: <subscription_tiers.updatedAt>` + mandatory reason. Audited.
   */
  editTierPricing: async (
    code: string,
    body: EditTierPricingBody,
    ifMatch: string,
  ): Promise<AdminSubscriptionTier> => {
    const res = await adminFetch<DataEnvelope<AdminSubscriptionTier>>(
      `/admin/subscription-tiers/${encodeURIComponent(code)}`,
      {
        method: 'PUT',
        headers: { 'If-Match': ifMatch },
        body: body as unknown as Record<string, unknown>,
      },
    );
    return res.data;
  },

  /** GET /admin/venue-subscriptions — paginated list (filter tier/status/venue). */
  listVenueSubscriptions: async (
    opts: AdminVenueSubscriptionListOptions = {},
  ): Promise<AdminVenueSubscriptionListResponse> => {
    return adminFetch<AdminVenueSubscriptionListResponse>(
      `/admin/venue-subscriptions${buildQuery({
        page: opts.page,
        perPage: opts.perPage,
        tierCode: opts.tierCode,
        status: opts.status,
        q: opts.q,
      })}`,
    );
  },

  /** GET /admin/venue-subscriptions/:venueId — detail + plan_history + invoices. */
  getVenueSubscription: async (venueId: string): Promise<AdminVenueSubscriptionDetail | null> => {
    try {
      const res = await adminFetch<DataEnvelope<AdminVenueSubscriptionDetail>>(
        `/admin/venue-subscriptions/${encodeURIComponent(venueId)}`,
      );
      return res.data;
    } catch (err) {
      if (
        err !== null &&
        typeof err === 'object' &&
        'status' in err &&
        (err as { status: number }).status === 404
      ) {
        return null;
      }
      throw err;
    }
  },

  /**
   * PUT /admin/venue-subscriptions/:venueId/override — override tier/cadence/
   * status with a mandatory reason. Appends history + a tier-derived rate row
   * (server-side). Gated on `venue_subscription:override`.
   */
  overrideSubscription: async (
    venueId: string,
    body: AdminOverrideSubscriptionBody,
  ): Promise<AdminVenueSubscriptionCore> => {
    const res = await adminFetch<DataEnvelope<AdminVenueSubscriptionCore>>(
      `/admin/venue-subscriptions/${encodeURIComponent(venueId)}/override`,
      { method: 'PUT', body: body as unknown as Record<string, unknown> },
    );
    return res.data;
  },

  /** POST /admin/venue-subscriptions/:venueId/grant-trial — case-by-case trial grant. */
  grantTrial: async (
    venueId: string,
    body: GrantTrialBody,
  ): Promise<AdminVenueSubscriptionCore> => {
    const res = await adminFetch<DataEnvelope<AdminVenueSubscriptionCore>>(
      `/admin/venue-subscriptions/${encodeURIComponent(venueId)}/grant-trial`,
      { method: 'POST', body: body as unknown as Record<string, unknown> },
    );
    return res.data;
  },

  /**
   * POST /admin/venue-subscriptions/:venueId/manual-invoice — one-off charge.
   * The backend returns only the new statement id (the detail page refetches
   * the full detail — including the invoices table — after the mutation).
   */
  createManualInvoice: async (
    venueId: string,
    body: ManualInvoiceBody,
  ): Promise<{ readonly statementId: string }> => {
    const res = await adminFetch<DataEnvelope<{ readonly statementId: string }>>(
      `/admin/venue-subscriptions/${encodeURIComponent(venueId)}/manual-invoice`,
      { method: 'POST', body: body as unknown as Record<string, unknown> },
    );
    return res.data;
  },
};
